import JobLogOutputHeader from "~/components/job/Job/Log/Output/Header";
import JobLogOutputLine from "~/components/job/Job/Log/Output/Line";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import JobLogGroupStore from "~/stores/JobLogGroupStore";
import { Group, Job } from "~/stores/JobLogStore";
import { useVirtualizer, Virtualizer } from "@tanstack/react-virtual";
import { useLineNumber } from "~/components/build/Show/lib/useLineNumber";
import { LogSearchContext } from "~/components/job/Job/Log/LogSearch";

type LogLine = Group["lines"][number] & {
  type?: "line";
};

type GroupLogLine = Omit<Group, "lines"> & {
  type: "group";
};

interface Match {
  /**
   * The text of the match.
   */
  match: string;

  /**
   * The log line number of the match.
   */
  line: number;

  /**
   * The log line group ID of the match.
   */
  group?: string;

  /**
   * The position of the match within the entire search result.
   */
  position: number;
}

interface HighlightMatch extends Match {
  /**
   * The range of the match in the text node.
   */
  range: Range;
}

/**
 * Find all the matches within current virtualised range.
 *
 * For each of these matches, find the text node that contains the match and create a range object
 * which can be used to highlight the match.
 */
const getVirtualMatches = (
  virtualizer: Virtualizer<any, any>,
  matches: Match[],
  lines: Array<LogLine | GroupLogLine>,
): HighlightMatch[] => {
  if (!matches.length) {
    return [];
  }

  const { startIndex } = virtualizer.range || {};
  if (startIndex === undefined) {
    return [];
  }

  // Get the index of the first match within the range.
  const line = lines[startIndex];
  const matchIndexOffset = matches.findIndex((match) => match.line >= line.number);

  // No matches within this range.
  if (matchIndexOffset === -1) {
    return [];
  }

  // Get all the text nodes within the virtual rows that are visible in the viewport, making sure
  // that we start iterating from same line we've found the first match in.
  const virtualTextNodes = virtualizer
    .getVirtualItems()
    .filter((item) => (item.key as number) >= line.number)
    .reduce<Node[]>((acc, item) => {
      const el = virtualizer.elementsCache.get(item.key) as Node;
      if (!el) {
        return acc;
      }

      const treeWalker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const allTextNodes: Node[] = [];
      let currentNode = treeWalker.nextNode();
      while (currentNode) {
        allTextNodes.push(currentNode);
        currentNode = treeWalker.nextNode();
      }

      return acc.concat(allTextNodes);
    }, []);

  // Keep track of match index so we know where the match lives in the entire range.
  let matchIndex = matchIndexOffset;

  // Loop through the all text nodes present across the virtual rows and find the matches,
  // returning a range object for each match.
  return virtualTextNodes.flatMap((node) => {
    const text = node.textContent;

    if (!text) {
      return [];
    }

    const virtualMatches: { start: number; end: number; matchIndex: number }[] = [];
    let startPos = 0;

    while (startPos < text.length) {
      // Get the match text from the matches array.
      const search = matches[matchIndex]?.match;

      if (!search) {
        break;
      }

      const index = text.indexOf(search, startPos);
      if (index === -1) {
        break;
      }

      // Increment the match index for each match found.
      virtualMatches.push({
        start: index,
        end: index + search.length,
        matchIndex: matchIndex++,
      });
      startPos = index + search.length;
    }

    // Create a range object for each instance of search found in the text node.
    return virtualMatches.map(({ start, end, matchIndex }) => {
      const range = new Range();
      range.setStart(node, start);
      range.setEnd(node, end);

      return { ...matches[matchIndex], range };
    });
  });
};

function escapeRegex(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * A virtualised table of job log lines and group headers where only the lines that are
 * visible within the viewport are rendered.
 */
export const VirtualisedLogOutput = ({
  log,
  timestampsOn,
  onTimestampClick,
  timestampsInUTC,
}: {
  log: Job;
  timestampsOn: boolean | null | undefined;
  timestampsInUTC?: boolean | null | undefined;
  onTimestampClick: () => void;
}) => {
  const [expandedLogGroups, setExpandedLogGroups] = useState({
    ...JobLogGroupStore.state,
  });

  /**
   * Update internal state when any log groups change to re-calculate the lines to be displayed.
   *
   * Ideally we move away from event listeners and instead use a zustand store once we've phased out the
   * legacy stores.
   */
  useEffect(() => {
    const handleGroupChange = () => {
      setExpandedLogGroups({ ...JobLogGroupStore.state });
    };

    log.groups.forEach((group) => {
      JobLogGroupStore.addChangeListener(group.id, handleGroupChange);
      JobLogGroupStore.setDefault(group.id, group.expanded);
      setExpandedLogGroups({ ...JobLogGroupStore.state });
    });

    return () => {
      log.groups.forEach((group) => {
        JobLogGroupStore.removeChangeListener(group.id, handleGroupChange);
      });
    };
  }, [log.groups]);

  /**
   * Flatten log groups into an array of log lines and group headers to make virtualisation easier.
   */
  const lines = useMemo(() => {
    return log.groups.reduce<Array<LogLine | GroupLogLine>>((acc, group) => {
      const { lines, ...groupLine } = group;

      if (group.name) {
        acc.push({ ...groupLine, type: "group" });
      }

      if (expandedLogGroups[group.id] && lines?.length) {
        acc = acc.concat(group.lines);
      }
      return acc;
    }, []);
  }, [log.groups, expandedLogGroups]);

  const tableRef = useRef<HTMLTableElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  /**
   * Calculate the scroll margin based on the position of the table relative to the scroll element.
   */
  useEffect(() => {
    const table = tableRef.current;
    if (!table) {
      return;
    }

    const observer = new ResizeObserver(() => {
      const element = rowVirtualizer.scrollElement;
      if (!element) {
        return;
      }

      setScrollMargin(
        table.getBoundingClientRect().top + element.scrollTop - element.getBoundingClientRect().top,
      );
    });

    observer.observe(table);
    return () => {
      observer.disconnect();
    };
  }, []);

  const getItemKey = useCallback((index: number) => lines[index].number, [lines]);

  const exampleCharRef = useRef<HTMLSpanElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: lines.length,

    getItemKey,

    // FIXME: This might be better as something passed in from the parent.
    getScrollElement: useCallback(() => document.querySelector("#tab-scroll-container"), []),

    // Callback to estimate the size of each row, calculated based on the width of a single character. This doesn't have to be
    // perfect as the virtualizer will measure the actual size of each row when it's rendered. It does however need to be roughly
    // in the ballpark as it helps with initial rendering performance and reduces the number of re-renders which in some cases
    // can cause React to throw an error.
    estimateSize: useCallback(
      (index) => {
        const charWidth = exampleCharRef.current?.clientWidth || 0;
        const charHeight = exampleCharRef.current?.clientHeight || 0;
        const defaultHeight = 36; // Default height for log lines (roughly 2x the height of a single line of text).

        // For log lines, we can estimate the height based on the text content.
        if (tableRef.current && charWidth && charHeight) {
          const line = lines[index];
          const textLength = line.textContent.length;
          const estimatedHeight =
            Math.ceil(textLength / (tableRef.current.clientWidth / charWidth)) * charHeight;

          return estimatedHeight;
        }

        return charHeight || defaultHeight;
      },
      [lines],
    ),

    scrollPaddingStart: scrollMargin,
    scrollMargin: scrollMargin,

    // Render overscan rows to avoid flickering when scrolling.
    overscan: 10,
  });

  // Get the current search term and position from the context.
  const { search, setTotalMatches, position } = useContext(LogSearchContext);

  /**
   * Find all the matches in the log lines and group headers
   */
  const { matches, groups } = useMemo(() => {
    const groups = new Set<string>();

    if (!search) {
      return { matches: [], groups };
    }

    // TODO: This is the spot to change to support regex searching, probably
    // by accepting a flag to switch between regex and non-regex searching.
    const regex = new RegExp(escapeRegex(search), "gi");
    const matchText = (text: string) => {
      return text.match(regex);
    };

    let position = 0;
    const matches = log.groups.reduce<Match[]>((acc, group) => {
      const matches = matchText(group.textContent);
      if (matches) {
        acc.push(
          ...matches.map((match) => ({
            match,
            line: group.number,
            position: ++position,
          })),
        );
      }

      group.lines.forEach((line) => {
        const matches = matchText(line.textContent);
        if (matches) {
          acc.push(
            ...matches.map((match) => ({
              match,
              line: line.number,
              position: ++position,
              group: group.id,
            })),
          );
          groups.add(group.id);
        }
      });

      return acc;
    }, []);

    return { matches, groups };
  }, [search]);

  /**
   * Expand all the groups with matches in them.
   */
  useEffect(() => {
    for (const group of groups) {
      JobLogGroupStore.set(group, true);
    }
  }, [groups]);

  /**
   * Highlight the search results when the search term or virtualised rows change.
   */
  useEffect(() => {
    clearHighlights();

    const virtualMatches = getVirtualMatches(rowVirtualizer, matches, lines);
    if (!matches.length) {
      return;
    }

    highlightMatches(virtualMatches, position);
  }, [matches, rowVirtualizer.range, position, lines]);

  /**
   * Clear all the highlights in the log lines.
   */
  const clearHighlights = useCallback(() => {
    if (CSS.highlights) {
      CSS.highlights.clear();
    } else {
      // Fallback for browsers that don't support the CSS Highlight API (ie. Firefox)
      rowVirtualizer.scrollElement?.querySelectorAll(".search-match").forEach((el) => {
        const parent = el.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(el.textContent || ""), el);
          parent.normalize();
        }
      });
    }
  }, [rowVirtualizer.scrollElement]);

  /**
   * Highlight search matches in the log lines.
   */
  const highlightMatches = useCallback((matches: HighlightMatch[], currentPosition: number) => {
    if (CSS.highlights) {
      CSS.highlights.set("search-match", new Highlight(...matches.map((match) => match.range)));
      const active = matches.find((match) => match.position === currentPosition);
      if (active) {
        CSS.highlights.set("search-match--active", new Highlight(active.range));
      }
    } else {
      // Fallback for browsers that don't support the CSS Highlight API (ie. Firefox)
      for (const match of matches) {
        const highlight = document.createElement("span");
        highlight.classList.add("search-match");
        if (match.position === currentPosition) {
          highlight.classList.add("search-match--active");
        }
        match.range.surroundContents(highlight);
      }
    }
  }, []);

  /**
   * Reset the current match when the search term changes.
   */
  useEffect(() => {
    setTotalMatches(matches.length);
  }, [matches.length, search]);

  /**
   * Scroll to the current match position when it changes.
   */
  useEffect(() => {
    if (!matches.length || position === 0) {
      return;
    }

    const match = matches[position - 1];
    if (!match) {
      return;
    }

    const virtualIndex = lines.findIndex((line) => line.number === match.line);

    rowVirtualizer.scrollToIndex(virtualIndex, { align: "center" });
  }, [position, matches]);

  /**
   * When a group/line number exists in the URL hash, scroll to it, and update the scroll
   * position if the URL hash changes
   */
  const { group, lineNumber } = useLineNumber();
  const logLineNumber = lineNumber ?? group;

  // Since lines vary based on expanded/collapsed groups, we need to hook into `lines` in our useEffect
  // below which means it can fire multiple times. To avoid repeated scrolling, keep track of the previous scroll
  // position and bail if the number hasn't changed.
  const prevScrollNumber = useRef<number | null>(null);

  useEffect(() => {
    if (logLineNumber && logLineNumber !== prevScrollNumber.current) {
      const virtualIndex = lines.findIndex((line) => line.number === logLineNumber);

      // Only scroll if we actually found the line in the expanded groups
      if (virtualIndex !== -1) {
        // If we found the line, update the previous scroll number and scroll to it
        prevScrollNumber.current = logLineNumber;

        // Small delay to allow the DOM to update before scrolling
        setTimeout(() => {
          rowVirtualizer.scrollToIndex(virtualIndex, { align: "start" });
        }, 0);
      }
    }
  }, [logLineNumber, lines]);

  const lineNumberWidth = log.totalLines?.toString().length ?? 0;
  const style = {
    "--line-number-width": `${Math.max(lineNumberWidth, 2)}ch`,
    height: `${rowVirtualizer.getTotalSize()}px`,
    position: "relative",
  } as React.CSSProperties;

  return (
    <>
      <span className="invisible absolute font-mono" ref={exampleCharRef}>
        A
      </span>
      <table ref={tableRef} className="JobLogOutputComponent__Table" style={style} cellPadding="0">
        <tbody>
          {rowVirtualizer.getVirtualItems().map((virtualItem) => {
            const virtualizerProps = {
              "data-index": virtualItem.index,
              style: {
                position: "absolute",
                width: "100%",
                left: 0,
                top: 0,
                transform: `translateY(${virtualItem.start - rowVirtualizer.options.scrollMargin}px)`,
              } as React.CSSProperties,
            };

            const line = lines[virtualItem.index];

            switch (line.type) {
              case "group":
                return (
                  <JobLogOutputHeader
                    key={virtualItem.key}
                    innerRef={rowVirtualizer.measureElement}
                    timestampsOn={timestampsOn}
                    timestampsInUTC={timestampsInUTC}
                    onTimestampClick={onTimestampClick}
                    durationPercentage={line.durationPercentage}
                    startedAt={line.startedAt}
                    finishedAt={line.finishedAt}
                    name={line.name}
                    id={line.id}
                    number={line.number}
                    finished={line.finished}
                    deemphasized={line.deemphasized}
                    {...virtualizerProps}
                  />
                );
              default:
                return (
                  <JobLogOutputLine
                    key={virtualItem.key}
                    innerRef={rowVirtualizer.measureElement}
                    timestampsOn={timestampsOn}
                    timestampsInUTC={timestampsInUTC}
                    onTimestampClick={onTimestampClick}
                    id={line.id}
                    number={line.number}
                    line={line.line}
                    startedAt={line.startedAt}
                    {...virtualizerProps}
                  />
                );
            }
          })}
        </tbody>
      </table>
    </>
  );
};
